import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Briefcase,
  Users,
  Rocket,
  Globe,
  Quote,
  ArrowRight,
} from "lucide-react";

const allJobs = [
  {
    id: "account-exec",
    title: "Account Executive",
    location: "Remote / US",
    type: "Full-time",
    category: "Sales",
    salary: "$80,000 - $120,000",
    responsibilities: [
      "Build and maintain relationships with restaurant partners.",
      "Drive sales growth by onboarding new restaurants.",
      "Collaborate with marketing to develop campaigns.",
    ],
    requirements: [
      "3+ years of sales experience, preferably in SaaS or food tech.",
      "Strong communication and negotiation skills.",
      "Ability to work independently in a remote setting.",
    ],
  },
  {
    id: "fullstack-dev",
    title: "Full-Stack Developer",
    location: "Remote / North America",
    type: "Full-time",
    category: "Engineering",
    salary: "$100,000 - $150,000",
    responsibilities: [
      "Develop and maintain FullPlateNow’s web platform.",
      "Integrate APIs with third-party delivery services.",
      "Collaborate with the design team to implement features.",
    ],
    requirements: [
      "5+ years of experience in full-stack development.",
      "Proficiency in React, Node.js, and TypeScript.",
      "Experience with API integrations and cloud infrastructure.",
    ],
  },
  {
    id: "support-specialist",
    title: "Restaurant Support Specialist",
    location: "Remote / Flexible",
    type: "Part-time",
    category: "Support",
    salary: "$30,000 - $50,000 (pro-rated)",
    responsibilities: [
      "Assist restaurant partners with onboarding and issues.",
      "Provide support via email, chat, and phone.",
      "Document feedback to improve platform features.",
    ],
    requirements: [
      "1+ years of customer support experience.",
      "Knowledge of the restaurant industry is a plus.",
      "Excellent communication and problem-solving skills.",
    ],
  },
  {
    id: "product-designer",
    title: "Product Designer",
    location: "Remote / US",
    type: "Contract",
    category: "Design",
    salary: "$60,000 - $90,000 (contract rate)",
    responsibilities: [
      "Design user-friendly interfaces for the platform.",
      "Create wireframes, prototypes, and final designs.",
      "Work with engineering to ensure design fidelity.",
    ],
    requirements: [
      "3+ years of experience in product design.",
      "Proficiency in Figma, Sketch, or Adobe XD.",
      "Strong portfolio showcasing SaaS or tech design work.",
    ],
  },
];

const categories = ["All", "Engineering", "Sales", "Support", "Design"];

const Careers = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredJobs =
    selectedCategory === "All"
      ? allJobs
      : allJobs.filter((job) => job.category === selectedCategory);

  return (
    <div className="bg-white text-black font-sans">
      {/* Hero Section with Background Image */}
      <section
        className="relative bg-cover bg-center py-40 text-center"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.6)), url('https://images.unsplash.com/photo-1516321497487-e288fb19713f?auto=format&fit=crop&w=1920&q=80')`,
        }}
      >
        <div className="max-w-7xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-4 leading-tight text-white">
            Join the FullPlateNow Team
          </h1>
          <p className="text-lg md:text-xl text-white/80 mb-6 max-w-2xl mx-auto">
            We’re on a mission to empower local restaurants through seamless
            delivery solutions. Be part of a team that’s making a real impact.
          </p>
          <Link
            to="/careers/apply"
            className="inline-flex items-center gap-2 text-white bg-[#FF5A5F] px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#E04F55] transition-all duration-300 shadow-md hover:shadow-lg animate-fade-in"
          >
            See Open Positions
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Company Culture Section */}
      <section className="px-6 py-32 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center text-black animate-fade-in">
            Our Culture
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Innovation-Driven",
                desc: "We’re constantly pushing boundaries to create solutions that make a difference for restaurants.",
                icon: <Rocket className="w-8 h-8 text-[#20C997] mb-4" />,
              },
              {
                title: "Collaborative Team",
                desc: "Work with a passionate team that values open communication and diverse perspectives.",
                icon: <Users className="w-8 h-8 text-[#20C997] mb-4" />,
              },
              {
                title: "Global Impact",
                desc: "Join a remote-first team making a global impact, supporting restaurants worldwide.",
                icon: <Globe className="w-8 h-8 text-[#20C997] mb-4" />,
              },
            ].map((culture, idx) => (
              <div
                key={idx}
                className="bg-[#F5F5F5] p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 text-center animate-fade-in"
                style={{ animationDelay: `${idx * 200}ms` }}
              >
                {culture.icon}
                <h3 className="text-lg font-semibold mb-2 text-black">
                  {culture.title}
                </h3>
                <p className="text-sm text-[#666666]">{culture.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="px-6 py-32 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center text-black animate-fade-in">
            Open Roles
          </h2>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-8 animate-fade-in">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  selectedCategory === category
                    ? "bg-[#FF5A5F] text-white shadow-md"
                    : "bg-white text-black/80 border border-gray-200 hover:bg-[#FF5A5F] hover:text-white"
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Job Listings */}
          <div className="space-y-6">
            {filteredJobs.map((job, idx) => (
              <div
                key={job.id}
                className="p-6 border border-gray-200 rounded-xl hover:shadow-md transition-all duration-300 hover:scale-[1.02] bg-white animate-fade-in"
                style={{ animationDelay: `${idx * 200}ms` }}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-semibold text-black mb-2">
                      {job.title}
                    </h3>
                    <p className="text-sm text-[#666666] mb-2">
                      {job.location} • {job.type}
                    </p>
                    <p className="text-sm text-[#666666] mb-2">
                      Salary: {job.salary}
                    </p>
                    <div className="mb-2">
                      <p className="text-sm font-medium text-black">
                        Key Responsibilities:
                      </p>
                      <ul className="list-disc list-inside text-sm text-[#666666]">
                        {job.responsibilities.map((resp, idx) => (
                          <li key={idx}>{resp}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-black">
                        Requirements:
                      </p>
                      <ul className="list-disc list-inside text-sm text-[#666666]">
                        {job.requirements.map((req, idx) => (
                          <li key={idx}>{req}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <Briefcase className="w-6 h-6 text-[#FF5A5F]" />
                </div>
                <Link
                  to={`/careers/${job.id}`}
                  className="inline-block mt-4 text-[#FF5A5F] font-semibold text-sm hover:underline"
                >
                  Learn More
                </Link>
              </div>
            ))}

            {filteredJobs.length === 0 && (
              <p className="text-sm text-[#666666] text-center animate-fade-in">
                No jobs in this category yet. Check back soon!
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Team Spotlight */}
      <section className="px-6 py-32 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-12 text-black animate-fade-in">
            Team Spotlight
          </h2>
          <div className="bg-[#F5F5F5] p-8 rounded-xl shadow-sm max-w-2xl mx-auto animate-fade-in">
            <Quote className="w-8 h-8 text-[#FF5A5F] mx-auto mb-4" />
            <blockquote className="text-lg italic text-[#666666] mb-4">
              “Working at FullPlateNow has been an incredible experience. I get
              to solve real problems for restaurants every day, and the team’s
              support makes it even better.”
            </blockquote>
            <p className="font-semibold text-black">
              — Sarah M., Product Manager
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 py-32 text-center bg-gradient-to-b from-[#F5F5F5] to-white">
        <h2 className="text-4xl font-bold mb-4 text-black animate-fade-in">
          Ready to Join Us?
        </h2>
        <p className="text-lg text-[#666666] mb-8 max-w-xl mx-auto">
          Be part of a team that’s transforming the restaurant delivery
          landscape. Apply today or reach out to learn more!
        </p>
        <div className="flex justify-center gap-4 animate-fade-in">
          <Link
            to="/careers/apply"
            className="inline-flex items-center gap-2 text-white bg-[#FF5A5F] px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#E04F55] transition-all duration-300 shadow-md hover:shadow-lg"
          >
            Apply Now
            <ArrowRight className="w-5 h-5" />
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-black bg-white border border-gray-200 px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#FF5A5F] hover:text-white transition-all duration-300 shadow-md hover:shadow-lg"
          >
            Contact Us
          </Link>
        </div>
      </section>

      {/* CSS for Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.6s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default Careers;
